/**
 * Resizable panel width for the parent shell (#1705 item 7): the sidebar. (The
 * companion rail's width lived here too until 2026-09-18, when the rail left
 * the parent home — guideline §2.9.) One number in localStorage, keyed by user, and
 * applied as a CSS custom property so Tailwind's arbitrary-value classes
 * (`w-[var(--trf-sidebar-w,16rem)]`) read it with the default intact when the
 * user never dragged anything.
 *
 * Bounds are the product's, not the screen's: a sidebar narrower than 13rem
 * clips the longest nav label ("TRF Announcements" since 2026-09-18, when
 * "Support & Resources" became "Support"; the width stays), wider than 24rem
 * starves the reading column at 1024px.
 */

export const SIDEBAR_WIDTH = { min: 208, max: 384, fallback: 256 } as const;

export const SIDEBAR_WIDTH_VAR = '--trf-sidebar-w';

type Bounds = { min: number; max: number; fallback: number };

export function clampWidth(value: number, bounds: Bounds): number {
  if (!Number.isFinite(value)) return bounds.fallback;
  return Math.min(bounds.max, Math.max(bounds.min, Math.round(value)));
}

function key(userId: string, panel: 'sidebar'): string {
  return `trf:layout:${panel}-w:${userId || 'anon'}`;
}

export function readPanelWidth(userId: string, panel: 'sidebar'): number | null {
  try {
    const raw = window.localStorage.getItem(key(userId, panel));
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? clampWidth(n, SIDEBAR_WIDTH) : null;
  } catch {
    return null;
  }
}

export function writePanelWidth(userId: string, panel: 'sidebar', px: number): void {
  try {
    window.localStorage.setItem(key(userId, panel), String(px));
  } catch {
    // Storage unavailable — the width lasts for this page load only.
  }
}

/** Push the width onto the root element as a CSS variable (null clears it). */
export function applySidebarWidth(px: number | null): void {
  const root = document.documentElement;
  if (px == null) root.style.removeProperty(SIDEBAR_WIDTH_VAR);
  else root.style.setProperty(SIDEBAR_WIDTH_VAR, `${px}px`);
}
